import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LinkIcon from "@mui/icons-material/Link";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { toast } from "react-hot-toast";
import PdfViewerDialog from "../PdfViewerDialog";
import {
  fetchBinatedArtworkProofMapping,
  fetchDesignRepArtworkProofMapping,
  saveDesignRepArtworkProofMappings,
  reprocessBinatedRequestFiles,
  saveBinatedArtworkProofMappings,
  updateBinatedArtworkProofMapping,
  deleteBinatedArtworkProofMapping,
  replaceBinatedArtworkProofMappingWithFile,
} from "../../services/orders";
import {
  autoMatchArtworkFiles,
  findDuplicateSelections,
  getArtworkSelectionKey,
  getFilePreviewKind,
  isPreviewableFile,
} from "./matching";

const PREVIEW_FRAME_SX = {
  border: "1px solid #e5e7eb",
  borderRadius: 1.5,
  background: "#fff",
  overflow: "hidden",
  minHeight: 160,
};

function PreviewTile({ title, fileName, src, previewKind, onOpenPdf }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
        {title}
      </Typography>
      <Box sx={PREVIEW_FRAME_SX}>
        {src && previewKind === "image" ? (
          <Box
            component="img"
            src={src}
            alt={fileName || title}
            sx={{ display: "block", width: "100%", height: 180, objectFit: "contain", background: "#f8fafc" }}
          />
        ) : src && previewKind === "pdf" ? (
          <Box sx={{ p: 2, minHeight: 180, display: "grid", placeItems: "center", background: "#f8fafc" }}>
            <Stack spacing={1} alignItems="center">
              <PictureAsPdfIcon color="error" />
              <Typography variant="caption" sx={{ textAlign: "center" }}>
                PDF preview available in dialog.
              </Typography>
              <Button size="small" variant="outlined" onClick={onOpenPdf}>
                Open PDF Preview
              </Button>
            </Stack>
          </Box>
        ) : src ? (
          <Box sx={{ p: 2, minHeight: 180, display: "grid", placeItems: "center", background: "#f8fafc" }}>
            <Stack spacing={1} alignItems="center">
              <LinkIcon color="action" />
              <Typography variant="caption" sx={{ textAlign: "center" }}>
                Preview unavailable; use open/download link.
              </Typography>
              <Button size="small" variant="outlined" href={src} target="_blank" rel="noopener noreferrer">
                Open File
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ p: 2, minHeight: 180, display: "grid", placeItems: "center", background: "#f8fafc" }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              No preview available.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function formatExtractionSummary(extraction = null) {
  if (!extraction) return "No extraction summary yet.";
  const parts = [
    `Status: ${String(extraction.status || "pending").replace(/_/g, " ")}`,
    `Files: ${Number(extraction.fileCount || 0)}`,
  ];
  if (Number(extraction.warningCount || 0) > 0) {
    parts.push(`Warnings: ${Number(extraction.warningCount || 0)}`);
  }
  if (extraction.extractedAt) {
    parts.push(`Extracted: ${new Date(extraction.extractedAt).toLocaleString()}`);
  }
  return parts.join(" | ");
}

function isRejectedMappedProof(mappedProof = null) {
  const status = String(
    mappedProof?.proofRow?.proofStatus || mappedProof?.proofRow?.status || ""
  ).toLowerCase();
  return status === "rejected";
}

function buildMissingContextWarning({
  orderId,
  documentType,
  documentId,
  requestId,
  normalizedStatus,
}) {
  const missing = [];
  if (!orderId) missing.push("orderId");
  if (!documentType) missing.push("documentType");
  if (!documentId) missing.push("documentId");
  if (!missing.length) return "";

  const details = [];
  if (requestId) details.push(`requestId: ${requestId}`);
  if (normalizedStatus) details.push(`status: ${normalizedStatus.replace(/_/g, " ")}`);

  return [
    `Binated artwork mapping cannot load yet because ${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} unavailable.`,
    details.length ? `Context: ${details.join(" | ")}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export default function BinatedArtworkProofMapper({
  active = false,
  orderId,
  documentType,
  documentId,
  lifecycle = null,
  disabled = false,
  onLifecycleRefresh,
}) {
  const [loading, setLoading] = useState(false);
  const [packagePickerOpen, setPackagePickerOpen] = useState(false);
  const [packageSelections, setPackageSelections] = useState({});
  const [savingPackage, setSavingPackage] = useState(false);
  const [mapperData, setMapperData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reprocessing, setReprocessing] = useState(false);
  const [savingKeys, setSavingKeys] = useState({});
  const [clearingKeys, setClearingKeys] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [selections, setSelections] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [allowDuplicateMapping, setAllowDuplicateMapping] = useState(false);
  const [pdfPreview, setPdfPreview] = useState({ open: false, src: "", fileName: "" });

  const requestId = lifecycle?.requestId || lifecycle?.id || mapperData?.requestId || null;
  const normalizedStatus = String(lifecycle?.normalizedStatus || mapperData?.normalizedStatus || "").toLowerCase();
  const trimmedOrderId = String(orderId || "").trim();
  const trimmedDocumentType = String(documentType || "").trim();
  const trimmedDocumentId = String(documentId || "").trim();
  const missingContextWarning = React.useMemo(
    () =>
      buildMissingContextWarning({
        orderId: trimmedOrderId,
        documentType: trimmedDocumentType,
        documentId: trimmedDocumentId,
        requestId,
        normalizedStatus,
      }),
    [normalizedStatus, requestId, trimmedDocumentId, trimmedDocumentType, trimmedOrderId]
  );
  const hasRequiredContext = !missingContextWarning;

  const loadMapperData = React.useCallback(async () => {
    if (!hasRequiredContext) return;
    try {
      setLoading(true);
      setLoadError("");
      const response = await fetchBinatedArtworkProofMapping(trimmedOrderId, {
        documentType: trimmedDocumentType,
        documentId: trimmedDocumentId,
        ...(requestId ? { requestId } : {}),
      });
      setMapperData(response || null);
      setSelections((prev) => {
        const next = { ...prev };
        (response?.artworks || []).forEach((artwork) => {
          const key = getArtworkSelectionKey(artwork);
          if (!next[key]) {
            next[key] = artwork?.mappedProof?.sourceFileId || "";
          }
        });
        return next;
      });
    } catch (error) {
      setLoadError(error?.response?.data?.message || "Failed to load artwork proof mapping.");
    } finally {
      setLoading(false);
    }
  }, [hasRequiredContext, requestId, trimmedDocumentId, trimmedDocumentType, trimmedOrderId]);

  useEffect(() => {
    if (!active) return;
    loadMapperData();
  }, [active, loadMapperData]);

  const artworks = Array.isArray(mapperData?.artworks) ? mapperData.artworks : [];
  const allFiles = Array.isArray(mapperData?.files) ? mapperData.files : [];
  const extractedFiles = allFiles.filter((file) => file?.source === "binated_zip");
  const files = extractedFiles.length > 0 ? extractedFiles : allFiles;
  // Only customer-facing proof previews belong in Complete Package.
  const packageProofFiles = extractedFiles.filter((file) => file?.isPdf || file?.isImage || /\.(pdf|png|jpe?g|webp|svg)$/i.test(String(file?.fileName || "")));
  const extraction = mapperData?.extraction || null;
  const hasExtractedFiles = extractedFiles.length > 0;
  const statusItems = Array.isArray(lifecycle?.statusItems) ? lifecycle.statusItems : [];
  const hasReadyStatusItems = statusItems.some((item) =>
    ["ready", "completed"].includes(String(item?.normalizedStatus || "").toLowerCase())
  );
  const lifecycleCompleted = ["completed", "ready"].includes(normalizedStatus);

  const selectionEntries = artworks.map((artwork) => {
    const key = getArtworkSelectionKey(artwork);
    const selectedSourceFileId = selections[key] ?? artwork?.mappedProof?.sourceFileId ?? "";
    return {
      key,
      artwork,
      sourceFileId: selectedSourceFileId,
    };
  });
  const duplicateEntries = findDuplicateSelections(selectionEntries);
  const duplicateFileIds = new Set(duplicateEntries.map((entry) => entry.sourceFileId));

  const fileUsage = {};
  selectionEntries.forEach((entry) => {
    if (!entry.sourceFileId) return;
    fileUsage[entry.sourceFileId] = (fileUsage[entry.sourceFileId] || 0) + 1;
  });

  const missingMappingsCount = selectionEntries.filter((entry) => !entry.sourceFileId).length;

  const saveCompletePackageSelections = async () => {
    const selected = artworks.map((artwork) => ({
      artwork,
      file: packageProofFiles.find((file) => file.fileId === packageSelections[getArtworkSelectionKey(artwork)]),
    })).filter((entry) => entry.file);
    if (!selected.length) {
      toast.error("Select at least one Binated proof file.");
      return;
    }
    const toId = (value) => {
      const n = Number(value);
      return Number.isSafeInteger(n) && n > 0 ? n : null;
    };
    if (selected.some(({ artwork }) => ![artwork.artworkId, artwork.orderItemId, artwork.decorationId].some((id) => toId(id)))) {
      toast.error("A selected artwork has no valid target ID. Map that artwork through Proofing instead.");
      return;
    }
    try {
      setSavingPackage(true);
      const existing = await fetchDesignRepArtworkProofMapping(trimmedDocumentType, trimmedDocumentId, {
        orderId: trimmedOrderId,
        sourceType: "design_rep",
      });
      const active = (Array.isArray(existing?.mappings) ? existing.mappings : []).filter((mapping) => mapping?.isActive !== false);
      if (active.some((mapping) => mapping.approvalMode !== "complete_package")) {
        toast.error("This Sales Order uses per-location proofing. Existing approval mode was not changed.");
        return;
      }
      if (active.some((mapping) => {
        const proof = mapping?.proofRow;
        const userState = String(proof?.approvals?.user?.state || "pending").toLowerCase();
        const clientState = String(proof?.approvals?.client?.state || "pending").toLowerCase();
        return proof?.customerActionLocked || userState !== "pending" || clientState !== "pending" || ["approved", "rejected"].includes(String(proof?.proofStatus || proof?.status || "").toLowerCase());
      })) {
        toast.error("Proof review has already started. Use the existing Proofing revision workflow.");
        return;
      }
      const entries = active.map((mapping) => ({
        sourceFileId: mapping.sourceFileId,
        sourceFileName: mapping.sourceFileName,
        sourceFilePath: mapping.sourceFilePath,
        sourceFileMeta: mapping.sourceFileMeta || undefined,
        proofScope: mapping.proofScope || "selected_targets",
        appliesTo: mapping.appliesTo || { scope: "order" },
      }));
      let added = 0;
      selected.forEach(({ artwork, file }) => {
        const artworkId = toId(artwork.artworkId);
        const orderItemId = toId(artwork.orderItemId);
        const decorationId = toId(artwork.decorationId);
        const filePath = file.downloadUrl || file.previewUrl;
        if (!filePath || !file.fileId) return;
        const alreadyMapped = entries.some((entry) =>
          String(entry.sourceFileId) === String(file.fileId) &&
          artworkId && (entry.appliesTo?.artworkIds || []).map(Number).includes(artworkId)
        );
        if (alreadyMapped) return;
        entries.push({
          sourceFileId: file.fileId,
          sourceFileName: file.fileName,
          sourceFilePath: filePath,
          sourceFileMeta: file,
          proofScope: "selected_targets",
          artworkId: artworkId || undefined,
          orderItemId: orderItemId || undefined,
          decorationId: decorationId || undefined,
          appliesTo: {
            scope: "decorations",
            artworkIds: artworkId ? [artworkId] : [],
            orderItemIds: orderItemId ? [orderItemId] : [],
            decorationIds: decorationId ? [decorationId] : [],
            purchaseOrderIds: [], supplierIds: [], decoratorIds: [],
          },
        });
        added += 1;
      });
      if (!added) {
        toast("No new proof files to add.");
        return;
      }
      await saveDesignRepArtworkProofMappings(trimmedDocumentType, trimmedDocumentId, {
        orderId: trimmedOrderId,
        sourceType: "design_rep",
        sourceProvider: "design_rep",
        approvalMode: "complete_package",
        approvalGroupId: active[0]?.approvalGroupId || undefined,
        proofScope: "selected_targets",
        packageFiles: entries,
      });
      toast.success(`${added} Binated proof file${added === 1 ? "" : "s"} added to Complete Package Proofing.`);
      setPackagePickerOpen(false);
      setPackageSelections({});
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not add Binated files to Complete Package Proofing.");
    } finally {
      setSavingPackage(false);
    }
  };

  const handleAutoMatch = () => {
    const nextSuggestions = autoMatchArtworkFiles(artworks, files);
    setSuggestions(nextSuggestions);
    setSelections((prev) => {
      const next = { ...prev };
      Object.entries(nextSuggestions).forEach(([key, suggestion]) => {
        if (suggestion?.sourceFileId) {
          next[key] = suggestion.sourceFileId;
        }
      });
      return next;
    });
    toast.success("Auto-match suggestions applied. Review and save when ready.");
  };

  const handleReprocess = async () => {
    if (!requestId) {
      toast.error("No Binated request ID is available yet.");
      return;
    }
    if (hasExtractedFiles) {
      const confirmed = window.confirm(
        "Reprocess ZIP files? Existing mappings will remain, but you may need to review them afterward."
      );
      if (!confirmed) return;
    }
    try {
      setReprocessing(true);
      const response = await reprocessBinatedRequestFiles(requestId);
      if (Array.isArray(response?.extraction?.warnings) && response.extraction.warnings.length > 0) {
        toast.error("ZIP reprocess completed with warnings. Review the extraction summary below.");
      } else {
        toast.success("ZIP files processed.");
      }
      if (typeof onLifecycleRefresh === "function") {
        await onLifecycleRefresh();
      }
      await loadMapperData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to process ZIP files.");
    } finally {
      setReprocessing(false);
    }
  };

  const saveSingleMapping = async (artwork) => {
    const key = getArtworkSelectionKey(artwork);
    const sourceFileId = selections[key] ?? artwork?.mappedProof?.sourceFileId ?? "";
    if (!sourceFileId) {
      toast.error("Select a Binated file before saving.");
      return;
    }
    if (duplicateFileIds.has(sourceFileId) && !allowDuplicateMapping) {
      toast.error("This file is already selected for another artwork. Enable duplicate mapping to continue.");
      return;
    }

    try {
      setSavingKeys((prev) => ({ ...prev, [key]: true }));
      if (
        artwork?.mappedProof?.mappingId &&
        isRejectedMappedProof(artwork?.mappedProof) &&
        sourceFileId !== (artwork?.mappedProof?.sourceFileId || "")
      ) {
        const confirmed = window.confirm(
          "Replace the rejected proof with this revised Binated file? The old rejected proof will be preserved as history and the new proof will start pending approval."
        );
        if (!confirmed) return;
        await replaceBinatedArtworkProofMappingWithFile(artwork.mappedProof.mappingId, {
          requestId,
          sourceFileId,
          reason: "customer_requested_changes",
          notes: "Revised file from Binated after customer change request",
        });
      } else if (artwork?.mappedProof?.mappingId) {
        await updateBinatedArtworkProofMapping(artwork.mappedProof.mappingId, {
          sourceFileId,
          artworkId: artwork.artworkId,
          orderItemId: artwork.orderItemId,
          decorationId: artwork.decorationId,
        });
      } else {
        await saveBinatedArtworkProofMappings(trimmedOrderId, {
          documentType: trimmedDocumentType,
          documentId: trimmedDocumentId,
          requestId,
          mappings: [
            {
              artworkId: artwork.artworkId,
              orderItemId: artwork.orderItemId,
              decorationId: artwork.decorationId,
              sourceFileId,
            },
          ],
        });
      }
      toast.success(
        artwork?.mappedProof?.mappingId &&
          isRejectedMappedProof(artwork?.mappedProof) &&
          sourceFileId !== (artwork?.mappedProof?.sourceFileId || "")
          ? "Revised proof mapped. Customer approval is pending."
          : "Artwork proof mapping saved."
      );
      await loadMapperData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save artwork proof mapping.");
    } finally {
      setSavingKeys((prev) => ({ ...prev, [key]: false }));
    }
  };

  const saveAllMappings = async () => {
    const changedRows = selectionEntries.filter((entry) => {
      const mappedSource = entry.artwork?.mappedProof?.sourceFileId || "";
      return entry.sourceFileId && entry.sourceFileId !== mappedSource;
    });

    if (!changedRows.length) {
      toast("No new mapping changes to save.");
      return;
    }
    if (!allowDuplicateMapping && duplicateEntries.length > 0) {
      toast.error("Resolve duplicate file selections before saving all.");
      return;
    }

    const revisionReplacementCount = changedRows.filter(
      (entry) =>
        entry.artwork?.mappedProof?.mappingId &&
        isRejectedMappedProof(entry.artwork?.mappedProof) &&
        entry.sourceFileId !== (entry.artwork?.mappedProof?.sourceFileId || "")
    ).length;
    if (revisionReplacementCount > 0) {
      const confirmed = window.confirm(
        `Create ${revisionReplacementCount} revised artwork proof mapping${revisionReplacementCount === 1 ? "" : "s"} and archive the currently rejected proof${revisionReplacementCount === 1 ? "" : "s"}?`
      );
      if (!confirmed) return;
    }

    try {
      setSavingAll(true);
      const createRows = changedRows.filter((entry) => !entry.artwork?.mappedProof?.mappingId);
      const updateRows = changedRows.filter((entry) => entry.artwork?.mappedProof?.mappingId);

      if (createRows.length) {
        await saveBinatedArtworkProofMappings(trimmedOrderId, {
          documentType: trimmedDocumentType,
          documentId: trimmedDocumentId,
          requestId,
          mappings: createRows.map((entry) => ({
            artworkId: entry.artwork.artworkId,
            orderItemId: entry.artwork.orderItemId,
            decorationId: entry.artwork.decorationId,
            sourceFileId: entry.sourceFileId,
          })),
        });
      }

      for (const entry of updateRows) {
        if (
          entry.artwork?.mappedProof?.mappingId &&
          isRejectedMappedProof(entry.artwork?.mappedProof) &&
          entry.sourceFileId !== (entry.artwork?.mappedProof?.sourceFileId || "")
        ) {
          await replaceBinatedArtworkProofMappingWithFile(entry.artwork.mappedProof.mappingId, {
            requestId,
            sourceFileId: entry.sourceFileId,
            reason: "customer_requested_changes",
            notes: "Revised file from Binated after customer change request",
          });
        } else {
          await updateBinatedArtworkProofMapping(entry.artwork.mappedProof.mappingId, {
            sourceFileId: entry.sourceFileId,
            artworkId: entry.artwork.artworkId,
            orderItemId: entry.artwork.orderItemId,
            decorationId: entry.artwork.decorationId,
          });
        }
      }

      toast.success("Artwork proof mappings saved.");
      await loadMapperData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save all artwork proof mappings.");
    } finally {
      setSavingAll(false);
    }
  };

  const clearMapping = async (artwork) => {
    const key = getArtworkSelectionKey(artwork);
    if (!artwork?.mappedProof?.mappingId) {
      setSelections((prev) => ({ ...prev, [key]: "" }));
      setSuggestions((prev) => ({ ...prev, [key]: null }));
      return;
    }

    try {
      setClearingKeys((prev) => ({ ...prev, [key]: true }));
      await deleteBinatedArtworkProofMapping(artwork.mappedProof.mappingId);
      toast.success("Artwork proof mapping cleared.");
      setSelections((prev) => ({ ...prev, [key]: "" }));
      await loadMapperData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to clear artwork proof mapping.");
    } finally {
      setClearingKeys((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (!active) return null;

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {(loading || reprocessing || savingAll) && <LinearProgress />}

      {loadError ? <Alert severity="error">{loadError}</Alert> : null}

      {missingContextWarning ? (
        <Alert severity="warning">
          {missingContextWarning}
          {!trimmedDocumentId ? " Binated document ID is unavailable, so artwork mapping cannot be loaded yet." : ""}
        </Alert>
      ) : null}

      {!lifecycle ? (
        <Alert severity="info">Submit a Binated art request to enable the artwork proof mapper.</Alert>
      ) : null}

      {lifecycle && !lifecycleCompleted && (
        <Alert severity="info">
          {normalizedStatus === "action_required"
            ? hasReadyStatusItems
              ? "Some placements need action, but ready placements can still be reviewed or mapped individually."
              : "At least one Binated placement is on Hold. Review the lifecycle status details and update artwork or instructions before resubmitting."
            : normalizedStatus === "processing" || normalizedStatus === "submitted"
            ? "Files will be available after Binated completes processing."
            : `Binated status is ${String(normalizedStatus || "pending").replace(/_/g, " ")}. Mapper will appear after completion.`}
        </Alert>
      )}

      {lifecycle && lifecycleCompleted && hasRequiredContext && !hasExtractedFiles && (
        <Alert
          severity={Number(extraction?.warningCount || 0) > 0 ? "warning" : "info"}
          action={
            requestId ? (
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                startIcon={<AutorenewIcon />}
                onClick={handleReprocess}
                disabled={disabled || reprocessing}
              >
                {reprocessing ? "Processing..." : "Process ZIP Files"}
              </Button>
            ) : null
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
            Completed request, but no extracted Binated ZIP files are available yet.
          </Typography>
          <Typography variant="caption">{formatExtractionSummary(extraction)}</Typography>
        </Alert>
      )}

      {lifecycle && lifecycleCompleted && hasRequiredContext && hasExtractedFiles && (
        <>
          <Alert
            severity={Number(extraction?.warningCount || 0) > 0 ? "warning" : "success"}
            action={
              requestId ? (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<AutorenewIcon />}
                  onClick={handleReprocess}
                  disabled={disabled || reprocessing}
                >
                  {reprocessing ? "Reprocessing..." : "Reprocess ZIP Files"}
                </Button>
              ) : null
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
              Binated files are ready for artwork mapping.
            </Typography>
            <Typography variant="caption">{formatExtractionSummary(extraction)}</Typography>
          </Alert>

          {Array.isArray(mapperData?.warnings) && mapperData.warnings.length > 0 ? (
            <Alert severity="warning">
              {mapperData.warnings.map((warning, index) => (
                <Typography key={`${warning?.code || "warning"}-${index}`} variant="caption" sx={{ display: "block" }}>
                  {warning?.message || "Warning"}
                </Typography>
              ))}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Artwork-to-Binated File Mapper
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Match each artwork target with a completed Binated proof file before downstream proof steps.
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {packageProofFiles.length > 0 && (
                <Button variant="outlined" onClick={() => setPackagePickerOpen(true)} disabled={disabled || savingPackage}>
                  Select files for Complete Package
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleAutoMatch}
                disabled={disabled || loading || !artworks.length || !files.length}
              >
                Auto-match
              </Button>
              <Button
                variant="contained"
                onClick={saveAllMappings}
                disabled={disabled || savingAll || !artworks.length || !files.length}
              >
                {savingAll ? "Saving..." : "Save All Mappings"}
              </Button>
            </Stack>
          </Box>

          {missingMappingsCount > 0 ? (
            <Alert severity="warning">
              {missingMappingsCount} artwork item{missingMappingsCount === 1 ? "" : "s"} do not have mapped proof files.
            </Alert>
          ) : null}

          {duplicateEntries.length > 0 ? (
            <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />}>
              {duplicateEntries.length} selection{duplicateEntries.length === 1 ? "" : "s"} reuse a Binated file.
              Enable duplicate mapping only if that is intentional.
            </Alert>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={allowDuplicateMapping}
                onChange={(event) => setAllowDuplicateMapping(event.target.checked)}
              />
            }
            label="Allow duplicate file mapping"
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.2fr) minmax(300px, 0.8fr)" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 680, overflow: "auto", pr: 0.5 }}>
              {artworks.map((artwork, index) => {
                const key = getArtworkSelectionKey(artwork);
                const selectedSourceFileId = selections[key] ?? artwork?.mappedProof?.sourceFileId ?? "";
                const selectedFile = files.find((file) => file.fileId === selectedSourceFileId) || null;
                const suggestion = suggestions[key];
                const previewKind = getFilePreviewKind(selectedFile || {});
                const duplicateSelected = selectedSourceFileId && duplicateFileIds.has(selectedSourceFileId);
                const isDirty = selectedSourceFileId !== (artwork?.mappedProof?.sourceFileId || "");
                const isSaving = Boolean(savingKeys[key]);
                const isClearing = Boolean(clearingKeys[key]);

                return (
                  <Card key={`${key}-${index}`} variant="outlined">
                    <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {artwork?.logoName || `Artwork ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                            Product/Order Item: {artwork?.orderItemId || "-"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                            Location: {artwork?.location || "-"} | Imprint: {artwork?.imprintType || "-"}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap">
                          {artwork?.mappedProof?.mappingId ? <Chip size="small" label="Mapped" color="success" /> : null}
                          {isRejectedMappedProof(artwork?.mappedProof) ? <Chip size="small" label="Rejected Proof" color="error" /> : null}
                          {isDirty ? <Chip size="small" label="Unsaved" color="warning" /> : null}
                          {suggestion?.confidence && suggestion.confidence !== "none" ? (
                            <Chip
                              size="small"
                              label={`Auto-match ${suggestion.confidence}`}
                              color={suggestion.confidence === "high" ? "success" : "info"}
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>
                      </Box>

                      <FormControl fullWidth size="small">
                        <InputLabel id={`binated-file-select-${index}`}>Mapped Binated File</InputLabel>
                        <Select
                          labelId={`binated-file-select-${index}`}
                          value={selectedSourceFileId}
                          label="Mapped Binated File"
                          onChange={(event) =>
                            setSelections((prev) => ({
                              ...prev,
                              [key]: String(event.target.value || ""),
                            }))
                          }
                        >
                          <MenuItem value="">
                            <em>Select a Binated file</em>
                          </MenuItem>
                          {files.map((file) => (
                            <MenuItem key={file.fileId} value={file.fileId}>
                              {file.fileName || file.zipEntryPath || file.fileId} - {file.fileType || file.extension || "file"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {duplicateSelected ? (
                        <Alert severity="warning">
                          This file is also selected for another artwork. Saving is disabled unless duplicate mapping is enabled.
                        </Alert>
                      ) : null}

                      {selectedFile && !isPreviewableFile(selectedFile) ? (
                        <Alert severity="info">Preview unavailable; use download/open link.</Alert>
                      ) : null}

                      {!selectedSourceFileId ? (
                        <Alert severity="warning">This artwork has no mapped Binated proof file.</Alert>
                      ) : null}

                      {artwork?.mappedProof?.mappingId &&
                      isRejectedMappedProof(artwork?.mappedProof) &&
                      selectedSourceFileId &&
                      selectedSourceFileId !== (artwork?.mappedProof?.sourceFileId || "") ? (
                        <Alert severity="info">
                          Saving this new Binated file will create a revised proof mapping and archive the old rejected proof.
                        </Alert>
                      ) : null}

                      {suggestion?.reasons?.length ? (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {suggestion.reasons.join(" | ")}
                        </Typography>
                      ) : null}

                      <Box sx={{ display: "flex", gap: 1.5, flexDirection: { xs: "column", md: "row" } }}>
                        <PreviewTile
                          title="Original Artwork"
                          fileName={artwork?.logoName}
                          src={artwork?.originalImage}
                          previewKind={getFilePreviewKind({ extension: String(artwork?.originalImage || "").split(".").pop() })}
                          onOpenPdf={() =>
                            setPdfPreview({
                              open: true,
                              src: artwork?.originalImage || "",
                              fileName: artwork?.logoName || "Original artwork",
                            })
                          }
                        />
                        <PreviewTile
                          title="Selected Binated Proof"
                          fileName={selectedFile?.fileName}
                          src={selectedFile?.previewUrl || selectedFile?.downloadUrl || ""}
                          previewKind={previewKind}
                          onOpenPdf={() =>
                            setPdfPreview({
                              open: true,
                              src: selectedFile?.previewUrl || selectedFile?.downloadUrl || "",
                              fileName: selectedFile?.fileName || "Binated proof",
                            })
                          }
                        />
                      </Box>

                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        {selectedFile ? (
                          <Button
                            size="small"
                            variant="text"
                            href={selectedFile?.downloadUrl || selectedFile?.previewUrl || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open File
                          </Button>
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => clearMapping(artwork)}
                          disabled={disabled || isClearing}
                        >
                          {isClearing ? "Clearing..." : "Clear"}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => saveSingleMapping(artwork)}
                          disabled={
                            disabled ||
                            isSaving ||
                            !selectedSourceFileId ||
                            (duplicateSelected && !allowDuplicateMapping)
                          }
                        >
                          {isSaving ? "Saving..." : "Save Mapping"}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            <Card variant="outlined" sx={{ maxHeight: 680, overflow: "auto" }}>
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Extracted Binated Files
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Completed ZIP assets available for mapping.
                </Typography>
                <Divider />
                {files.map((file) => {
                  const usageCount = fileUsage[file.fileId] || 0;
                  const previewKind = getFilePreviewKind(file);
                  return (
                    <Box
                      key={file.fileId}
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 1.5,
                        p: 1.25,
                        background: usageCount > 0 ? "#f8fafc" : "#fff",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                            {file.fileName || file.fileId}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", wordBreak: "break-word" }}>
                            {file.zipEntryPath || file.fileType || "Binated file"}
                          </Typography>
                        </Box>
                        {usageCount > 0 ? (
                          <Tooltip title={usageCount > 1 ? "Mapped to multiple artworks" : "Currently selected or mapped"}>
                            <Chip
                              size="small"
                              color={usageCount > 1 ? "warning" : "success"}
                              label={usageCount > 1 ? `Mapped x${usageCount}` : "Mapped"}
                            />
                          </Tooltip>
                        ) : null}
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        {previewKind === "image" ? (
                          <Box
                            component="img"
                            src={file.previewUrl || file.downloadUrl}
                            alt={file.fileName || "Binated file"}
                            sx={{ width: "100%", maxHeight: 180, objectFit: "contain", borderRadius: 1, background: "#f8fafc" }}
                          />
                        ) : previewKind === "pdf" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() =>
                              setPdfPreview({
                                open: true,
                                src: file.previewUrl || file.downloadUrl || "",
                                fileName: file.fileName || "PDF proof",
                              })
                            }
                          >
                            Preview PDF
                          </Button>
                        ) : (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Preview unavailable for this file type.
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                        <Chip size="small" label={file.extension || "file"} variant="outlined" />
                        {file.fileType ? <Chip size="small" label={file.fileType} variant="outlined" /> : null}
                      </Stack>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      <Dialog open={packagePickerOpen} onClose={() => !savingPackage && setPackagePickerOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Select Binated files for Complete Package</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Choose a customer-facing proof for each artwork location. Unselected locations are skipped. Existing proof files are preserved.
          </Typography>
          <Stack spacing={2}>
            {artworks.map((artwork, index) => {
              const key = getArtworkSelectionKey(artwork);
              const selected = packageProofFiles.find((file) => file.fileId === packageSelections[key]);
              return (
                <Box key={key || index} sx={{ display: "flex", gap: 1.5, flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{artwork.logoName || `Artwork ${index + 1}`}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>Product #{artwork.orderItemId || "-"} · {artwork.location || "Location not set"}</Typography>
                  </Box>
                  <FormControl size="small" sx={{ flex: 1.5, minWidth: 220 }}>
                    <InputLabel>Proof file</InputLabel>
                    <Select value={packageSelections[key] || ""} label="Proof file" onChange={(event) => setPackageSelections((prev) => ({ ...prev, [key]: event.target.value }))}>
                      <MenuItem value=""><em>Skip this location</em></MenuItem>
                      {packageProofFiles.map((file) => <MenuItem key={file.fileId} value={file.fileId}>{file.fileName}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {selected ? <Button size="small" href={selected.previewUrl || selected.downloadUrl} target="_blank" rel="noopener noreferrer">Preview</Button> : null}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackagePickerOpen(false)} disabled={savingPackage}>Cancel</Button>
          <Button variant="contained" onClick={saveCompletePackageSelections} disabled={savingPackage || !Object.values(packageSelections).some(Boolean)}>
            {savingPackage ? "Saving..." : "Add to Complete Package"}
          </Button>
        </DialogActions>
      </Dialog>
      <PdfViewerDialog
        open={pdfPreview.open}
        src={pdfPreview.src}
        fileName={pdfPreview.fileName}
        onClose={() => setPdfPreview({ open: false, src: "", fileName: "" })}
      />
    </Box>
  );
}
